import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, tap } from 'rxjs';

import { ApiUrlService } from './api-url.service';
import {
  CrearNuevaSolicitud,
  SolicitudDetalleResponse,
  SolicitudesListadoItem,
  SolicitudesListadoQuery,
  SolicitudesListadoResponse
} from '../models/solicitudes';
import { normalizarTextoMayusculasSinTildes } from '../utils/text-normalization';

@Injectable({
  providedIn: 'root'
})
export class AdminRequestsService {
  private readonly basePath = '/api/v1/admin/requests';
  private readonly pendientesRefreshSubject = new Subject<SolicitudesListadoItem[] | null>();
  readonly pendientesRefresh$ = this.pendientesRefreshSubject.asObservable();

  constructor(private apiUrl: ApiUrlService, private http: HttpClient) {}

  private resolveIdUsuario(source: Record<string, any>): string | null | undefined {
    return source?.['id_usuario']
      ?? source?.['idusuario']
      ?? source?.['idUsuario']
      ?? source?.['solicitud']?.['id_usuario']
      ?? source?.['solicitud']?.['idusuario']
      ?? source?.['solicitud']?.['idUsuario'];
  }

  private normalizeUpdatePayload(
    idSolicitud: number,
    solicitud: Partial<SolicitudDetalleResponse['solicitud']> & Record<string, any>
  ): Record<string, any> {
    return {
      ...solicitud,
      id_solicitud: solicitud?.['id_solicitud'] ?? solicitud?.['id'] ?? idSolicitud,
      id_usuario: this.resolveIdUsuario(solicitud),
      fecha_creacion: solicitud?.['fecha_creacion'] ?? solicitud?.['createdAt'] ?? solicitud?.['created_at'],
      fecha_actualizacion: solicitud?.['fecha_actualizacion'] ?? solicitud?.['updatedAt'] ?? solicitud?.['updated_at'],
      prescriptor: solicitud?.prescriptor == null
        ? null
        : normalizarTextoMayusculasSinTildes(solicitud.prescriptor),
      nombre_paciente: (solicitud?.['nombre_paciente'] ?? solicitud?.['nombrePaciente']) == null
        ? null
        : normalizarTextoMayusculasSinTildes(solicitud?.['nombre_paciente'] ?? solicitud?.['nombrePaciente'])
    };
  }

  public notificarPendientesRefresh(items?: SolicitudesListadoItem[]): void {
    this.pendientesRefreshSubject.next(items ?? null);
  }

  public obtenerListadoSolicitudes(query?: SolicitudesListadoQuery): Observable<SolicitudesListadoResponse> {
    const params = this.buildListadoParams(query);
    return this.http.get<SolicitudesListadoResponse>(this.apiUrl.build(this.basePath), { params });
  }

  public obtenerDetalleSolicitud(idSolicitud: number): Observable<SolicitudDetalleResponse> {
    return this.http.get<SolicitudDetalleResponse>(this.apiUrl.build(`${this.basePath}/${idSolicitud}`));
  }

  public crearSolicitud(payload: CrearNuevaSolicitud): Observable<SolicitudDetalleResponse> {
    const payloadNormalizado = {
      ...payload,
      prescriptor: normalizarTextoMayusculasSinTildes(payload.prescriptor),
      nombrePaciente: normalizarTextoMayusculasSinTildes(payload.nombrePaciente)
    };
    return this.http.post<SolicitudDetalleResponse>(this.apiUrl.build(this.basePath), payloadNormalizado)
      .pipe(tap(() => this.notificarPendientesRefresh()));
  }

  public actualizarDetalleSolicitud(
    idSolicitud: number,
    solicitud: Partial<SolicitudDetalleResponse['solicitud']> & Record<string, any>
  ): Observable<SolicitudDetalleResponse> {
    const payload = this.normalizeUpdatePayload(idSolicitud, solicitud);
    return this.http.put<SolicitudDetalleResponse>(this.apiUrl.build(`${this.basePath}/${idSolicitud}`), payload)
      .pipe(tap(() => this.notificarPendientesRefresh()));
  }

  public actualizarEstadoSolicitud(
    idSolicitud: number,
    estado: string,
    comentario?: string,
    idUsuario?: string | null
  ): Observable<SolicitudDetalleResponse> {
    const payload: Record<string, any> = { estado };
    const comentarioNormalizado = comentario?.trim();
    if (comentarioNormalizado) payload['comentario'] = comentarioNormalizado;
    if (idUsuario) payload['id_usuario'] = idUsuario;
    return this.http.put<SolicitudDetalleResponse>(
      this.apiUrl.build(`${this.basePath}/${idSolicitud}/status`),
      payload
    ).pipe(tap(() => this.notificarPendientesRefresh()));
  }

  public obtenerHistorialSolicitud(idSolicitud: number): Observable<any> {
    return this.http.get<any>(this.apiUrl.build(`${this.basePath}/${idSolicitud}/history`));
  }

  public obtenerImagenSolicitud(idSolicitud: number | string, cacheBuster?: number): Observable<Blob> {
    const url = this.apiUrl.build(`${this.basePath}/${idSolicitud}/attachment`);
    const options = cacheBuster
      ? { params: { t: String(cacheBuster) }, responseType: 'blob' as const }
      : { responseType: 'blob' as const };
    return this.http.get(url, options);
  }

  public subirImagenSolicitud(idSolicitud: number | string, imagen: File): Observable<{ message?: string }> {
    const payload = new FormData();
    payload.append('imagen', imagen, imagen.name);
    return this.http.post<{ message?: string }>(
      this.apiUrl.build(`${this.basePath}/${idSolicitud}/attachment`),
      payload
    );
  }

  public eliminarImagenSolicitud(idSolicitud: number | string): Observable<{ message?: string }> {
    return this.http.delete<{ message?: string }>(this.apiUrl.build(`${this.basePath}/${idSolicitud}/attachment`));
  }

  private buildListadoParams(query?: SolicitudesListadoQuery): HttpParams {
    let params = new HttpParams();
    if (!query) return params;

    const appendValue = (key: string, value: string | number | boolean | null | undefined): void => {
      if (value === null || value === undefined || value === '') return;
      params = params.append(key, String(value));
    };

    appendValue('view', query.view);
    appendValue('cliente_id', query.cliente_id);
    appendValue('q', query.q?.trim());
    appendValue('updated_days', query.updated_days);
    appendValue('limit', query.limit);
    appendValue('cursor', query.cursor);

    const estados = Array.isArray(query.estado) ? query.estado : (query.estado ? [query.estado] : []);
    estados.forEach((estado) => appendValue('estado', estado));

    return params;
  }
}
